import { useState } from 'react';

import { ArrowRightIcon, CheckIcon, CopyIcon, MailIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { EmailIntegration } from '@/shared/types/integration';
import { EMAIL_INBOUND_DOMAIN } from '@/util/constants';

interface ForwardingConfigurationProps {
  integration: EmailIntegration;
  /** Whether the user has write permissions */
  canWrite?: boolean;
  /** Callback when auto-detect is triggered */
  onAutoDetect?: () => void;
}

/**
 * ForwardingConfiguration - Email forwarding setup component
 *
 * Features:
 * - Displays unique Moveo forwarding email address
 * - Copy functionality for easy setup
 * - Instructions for email provider configuration
 * - Final verification step instructions
 * - Auto-detect functionality for verification
 *
 * @param {EmailIntegration} integration - Email integration configuration
 * @param {boolean} canWrite - Whether user has write permissions
 * @param {function} onAutoDetect - Callback for auto-detection
 * @returns {JSX.Element} The forwarding configuration component
 */
export const ForwardingConfiguration = ({ integration }: ForwardingConfigurationProps) => {
  const { t } = useTranslation();

  const forwardingEmail = `${integration.integration_id}@${EMAIL_INBOUND_DOMAIN}`;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(forwardingEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 4000);
  };

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Forwarding Section Title */}
      <div className="flex flex-col items-start justify-center pl-1">
        <h3 className="text-lg leading-7 font-semibold">{t('email.forwarding.title')}</h3>
      </div>

      {/* Main Container with Card */}
      <Card className="w-full shadow-none">
        <CardContent className="flex flex-row items-center justify-between gap-6 px-6">
          <div className="flex max-w-sm flex-1 flex-col gap-2">
            {/* Forwarding Email Address */}
            <div className="flex flex-col gap-2">
              <label className="text-sm leading-5 font-medium">
                {t('email.forwarding.moveo_address')}
              </label>
              <InputGroup size="md" className="w-full bg-background">
                <InputGroupInput type="text" value={forwardingEmail} readOnly className="text-sm" />
                <InputGroupAddon align="inline-end">
                  <InputGroupButton
                    type="button"
                    size="icon-sm"
                    onClick={handleCopy}
                    aria-label={t('common.copy')}
                    className="min-w-0"
                  >
                    {copied ? (
                      <CheckIcon className="text-muted-foreground" size={16} />
                    ) : (
                      <CopyIcon className="text-muted-foreground" size={16} />
                    )}
                  </InputGroupButton>
                </InputGroupAddon>
              </InputGroup>
            </div>

            {/* Instructions */}
            <p className="text-sm leading-5 text-muted-foreground">
              {t('email.forwarding.instructions')}
            </p>
          </div>

          <ArrowRightIcon size={24} className="shrink-0 text-muted-foreground" />

          {/* Final Step Alert */}
          <Alert className="max-w-sm flex-1 rounded-lg border border-border bg-background p-4">
            <MailIcon className="h-4 w-4 shrink-0" />
            <AlertDescription className="ml-2 min-w-0">
              <div className="flex flex-col gap-1">
                <p className="text-base leading-none font-medium">
                  {t('email.forwarding.final_step')}
                </p>
                <p className="text-sm leading-6">
                  {t('email.forwarding.check_analytics')}{' '}
                  <span className="font-medium">{t('email.forwarding.verification_email')}</span>{' '}
                  {t('email.forwarding.complete_setup')}
                </p>
                <p className="text-sm leading-6">
                  {t('email.forwarding.contact')}{' '}
                  <a
                    href="mailto:support@moveo.ai"
                    className="font-medium underline underline-offset-4"
                  >
                    {t('email.forwarding.customer_success')}
                  </a>{' '}
                  {t('email.forwarding.for_issues')}
                </p>
              </div>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};
